import classNames from "classnames";
import { Icon, IconValue } from "../../Icon/Icon";
import { useResizeObserver } from "client/Hooks";
import { setTrackingId } from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef
} from "react";
import styles from "./StepIndicator.module.less";

export type StepStatus =
	| "active"
	| "finished"
	| "hasError"
	| "unfinished";

const stepStatusToStyles: { [key in StepStatus]: string } = {
	["active"]: styles.root_active,
	["finished"]: styles.root_finished,
	["hasError"]: styles.root_hasError,
	["unfinished"]: styles.root_unfinished,
};

const stepStatusToIcon: { [key in StepStatus]: IconValue | null } = {
	["active"]: null,
	["finished"]: "check",
	["hasError"]: "triangle-exclamation",
	["unfinished"]: null,
};

export type StepProps = {
	stepNumber: number;
	title: string;
	status: StepStatus;
	onClick(): void;
	onNewWidth(newWidth: number): void;
	trackingId?: string;
};

export const StepIndicator = (props: StepProps): ReactElement => {
	const {
		stepNumber,
		title,
		status,
		onClick,
		onNewWidth,
		trackingId
	} = props;

	const liRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
	const { width, wasInitialized } = useResizeObserver(liRef);

	useEffect(
		() => {
			if (wasInitialized) {
				onNewWidth(width);
			}
		},
		[
			onNewWidth,
			wasInitialized,
			width
		]
	);

	const clickHandler = (): void => {
		if (status === "finished" || status === "hasError") {
			onClick();
		}
	};

	const icon = useMemo(
		(): IconValue | null => stepStatusToIcon[status],
		[status]
	);

	return (
		<li
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				stepStatusToStyles[status]
			)}
			onClick={clickHandler}
			ref={liRef}
		>
			<div className={styles.content}>
				<h4 className={styles.title}>{`${stepNumber}. ${title}`}</h4>
				<div className={styles.status}>
					<div className={styles.circle}>
						<span className={styles.icon}>
							{icon && <Icon value={icon}/>}
						</span>
					</div>
					<div className={styles.line}/>
				</div>
			</div>
		</li>
	);
};
StepIndicator.displayName = "StepIndicator";
